import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { get } from "../../utils/request";

const CartContext = createContext(null);

export const CART_UPDATED_EVENT = "teleport:cart-updated";

export function notifyCartUpdated() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CART_UPDATED_EVENT));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const isAuthenticated = useCallback(
    () => Boolean(typeof window !== "undefined" && localStorage.getItem("teleport_token")),
    []
  );

  const refreshCart = useCallback(async () => {
    if (!isAuthenticated()) {
      setItems([]);
      return [];
    }
    setLoading(true);
    try {
      const result = await get("/cart/listItems");
      const nextItems = Array.isArray(result) ? result : [];
      setItems(nextItems);
      return nextItems;
    } catch {
      setItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshCart();
    const refresh = () => refreshCart();
    window.addEventListener(CART_UPDATED_EVENT, refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [refreshCart]);

  const value = useMemo(
    () => ({
      items,
      count: items.filter((item) => item.status !== "Expired").length,
      loading,
      refreshCart,
      isAuthenticated,
    }),
    [items, loading, refreshCart, isAuthenticated]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
